import { useState, useCallback, useEffect } from 'react'
import classnames from 'classnames'
import Formsy from 'formsy-react'

import { withTranslate } from '@i18n/utils'

import useRequestPending from 'shared/hooks/useRequestPending'
import { isAddressFilled } from 'shared/utils'
import { useSendGTAGEvent } from 'analytics/utils'

import { CardSection } from '@components/CardSection'
import ErrorMessage from '@components/ErrorMessage'
import Button from '@components/Button'
import InfoPopup from '@components/InfoPopup'

import PaymentMethodSection from './PaymentMethodSection'
import FooterSection from './FooterSection'
import AddressSection from './AddressSection'

import { billingAddressSelector, deliveryAddressSelector, userSelector } from 'auth/selectors'
import { authVisible } from 'header/actions'
import { makePurchase } from 'basket/actions'
import { clearPending } from 'api/actions'

import { makePurchasePendingSelector } from 'basket/selectors'
import { selectCreditCard, selectVehicle } from 'booking/actions'
import { selectedCreditCardSelector, selectedSeasonTicketSelector, selectedVehicleSelector } from 'booking/selectors'
import { getPaymentMethod } from 'booking/utils'

import BoletoForm from 'purchase-flow/boleto/BoletoForm'

import styles from './styles.module.scss'

const ReviewPay = ({
    purchaseData,
    billingAddress,
    deliveryAddress,
    user,
    makePurchase,
    clearPending,
    selectedCreditCard,
    selectCreditCard,
    selectedVehicle,
    selectedSeasonTicket = {},
    makePurchasePending,
}) => {
    const sendGTAGEvent = useSendGTAGEvent()
    const { product, startDate, vehicles, details } = purchaseData
    const { rate } = selectedSeasonTicket

    const { error, isLoading } = useRequestPending(makePurchasePending)
    const isBillingAddressFilled = isAddressFilled(billingAddress)

    useEffect(() => {
        clearPending(['makePurchase'])
    }, [])

    /** BOLETO */
    const [isBoletoVisible, setIsBoletoVisible] = useState(false)
    const [formBoletoData, setFormBoletoData] = useState(null)
    const [isFormBoletoValid, setIsFormBoletoValid] = useState(false)
    const [isUsingBoleto, setIsUsingBoleto] = useState(false)

    const setCreditCard = useCallback(
        card => {
            selectCreditCard(card)
            setIsUsingBoleto(false)
        },
        [selectCreditCard, setUsingBoleto]
    )

    const setUsingBoleto = useCallback(() => {
        setIsUsingBoleto(true)
        selectCreditCard(null)
    }, [selectCreditCard, setUsingBoleto])

    const boletoClassName = classnames(styles.boleto, {
        [styles.selected]: isUsingBoleto,
    })

    const isAddBoletoButtonVisible = user?.details.document.type === 'CNPJ'

    const isPaymentSet = isUsingBoleto ? isFormBoletoValid : !!selectedCreditCard

    const onPayForParkingSubmit = () => {
        window.grecaptcha.ready(() => {
            window.grecaptcha.execute(GOOGLE_RECAPTCHA_KEY, { action: 'submit' }).then(token => {
                const boletoData = isUsingBoleto && isFormBoletoValid ? formBoletoData : null

                if (selectedCreditCard) {
                    sendGTAGEvent('payment_method_selected', {
                        payment_type: getPaymentMethod(selectedCreditCard.id),
                    })
                }

                makePurchase(
                    {
                        rate,
                        vehicles: selectedVehicle,
                        boletoData,
                        ...details,
                    },
                    token
                )
            })
        })
    }

    if (!rate) return null

    return (
        <CardSection>
            <>
                <div className={styles.productDetails}>
                    <span className="font-weight-bold">{product.displayValue}</span>
                    <span className="mt-1">
                        <span className={styles.lineDescription}>
                            <T>dashboard.subscriptions.products.label.plan.name</T>:{' '}
                        </span>
                        <span>{rate.rateName}</span>
                    </span>
                    <span className="mt-1">
                        <span className={styles.lineDescription}>
                            <T>dashboard.subscriptions.products.label.start</T>:{' '}
                        </span>
                        <span>{startDate.displayValue}</span>
                    </span>

                    <span className="mt-1">
                        <span className={styles.lineDescription}>
                            <T>dashboard.subscriptions.products.label.vehicles</T>:{' '}
                        </span>
                        <span>
                            {vehicles &&
                                vehicles.map((v, i) => (
                                    <span key={i}>
                                        {v.tag}
                                        {i < vehicles.length - 1 ? ', ' : ''}
                                    </span>
                                ))}
                        </span>
                    </span>
                </div>
                <hr className="my-4" />
                <div className={classnames('col-12', 'col-xs-9', 'text-sm-right', styles.reviewTotal)}>
                    <div className={classnames('d-flex', styles.total)}>
                        <div className="col-8 col-sm-9">
                            <T>subscription.purchase.invoice.total</T>{' '}
                        </div>
                        <div className="col-4 col-sm-3 text-right">
                            {rate.currencySymbol}
                            {rate.recurringAmount}
                        </div>
                    </div>
                </div>
                <AddressSection billingAddress={billingAddress} deliveryAddress={deliveryAddress} />
                <PaymentMethodSection
                    selectedPaymentMethod={selectedCreditCard}
                    setPaymentMethod={setCreditCard}
                    paymentMethods={user?.creditCards}
                />
                {isAddBoletoButtonVisible && !isBoletoVisible && (
                    <Button
                        size="small"
                        btnStyle="link"
                        iconLeft="plus-round"
                        onClick={() => {
                            setIsBoletoVisible(true)
                            setUsingBoleto()
                        }}>
                        <T>subscription.purchase.addboleto</T>
                    </Button>
                )}
                {isBoletoVisible && (
                    <div className={boletoClassName} onClick={setUsingBoleto}>
                        <div className="mb-3">
                            <div className={styles.boletoHeader}>
                                <h6 className={styles.boletoTitle}>
                                    <T>subscription.purchase.boleto</T>
                                </h6>
                                <InfoPopup
                                    className="ml-2"
                                    content={<T>subscription.purchase.boleto.info.content</T>}
                                    title={<T>subscription.purchase.boleto.info.title</T>}
                                />
                            </div>
                            <div className="text-small text-light">
                                <T>subscription.purchase.boleto.hint</T>
                            </div>
                        </div>
                        <BoletoForm
                            onChange={setFormBoletoData}
                            onValid={() => {
                                setIsFormBoletoValid(true)
                            }}
                            onInvalid={() => {
                                setIsFormBoletoValid(false)
                            }}
                        />
                    </div>
                )}
                <Formsy onSubmit={onPayForParkingSubmit}>
                    <FooterSection
                        disabled={isLoading || !isBillingAddressFilled || !isPaymentSet}
                        loading={isLoading}
                        total={`${rate.currencySymbol}${rate.amount}`}
                    />
                </Formsy>
            </>
            {error && <ErrorMessage className="mt-5" messages={[error]} />}
        </CardSection>
    )
}

export default compose(
    withTranslate,
    connect(
        createStructuredSelector({
            billingAddress: billingAddressSelector,
            deliveryAddress: deliveryAddressSelector,
            user: userSelector,
            selectedCreditCard: selectedCreditCardSelector,
            selectedVehicle: selectedVehicleSelector,
            selectedSeasonTicket: selectedSeasonTicketSelector,
            makePurchasePending: makePurchasePendingSelector,
        }),
        { authVisible, makePurchase, selectCreditCard, selectVehicle, clearPending }
    )
)(ReviewPay)
